import React, { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { bindIdpClient, createIdpClient, type IdpClient } from '@digiwedge/idp-client';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';

const IdpContext = createContext<IdpClient | null>(null);

type EnvMap = Record<string, string | undefined>;

const readEnv = (key: string): string | undefined => {
  const runtime = (globalThis as { __ENV__?: EnvMap }).__ENV__;
  if (runtime?.[key]) return runtime[key];
  const injected = (globalThis as { importMetaEnv?: EnvMap }).importMetaEnv;
  if (injected?.[key]) return injected[key];
  const proc = (globalThis as { process?: { env?: EnvMap } }).process?.env;
  return proc?.[key];
};

const resolveAudience = (): string => {
  try {
    const meta = document.querySelector('meta[name="idp-audience"]') as HTMLMetaElement | null;
    const fromMeta = meta?.content?.trim();
    if (fromMeta) return fromMeta;
  } catch {
    // ignore
  }
  const envAud = readEnv('VITE_IDP_AUDIENCE')?.trim();
  return envAud && envAud.length ? envAud : 'yolo-admin';
};

export function IdpProvider({ children }: PropsWithChildren) {
  const token = useGlobalStore((s: GlobalState) => s.token);
  const refreshToken = useGlobalStore((s: GlobalState) => s.refreshToken);
  const jti = useGlobalStore((s: GlobalState) => s.jti);
  const login = useGlobalStore((s: GlobalState) => s.login);
  const logout = useGlobalStore((s: GlobalState) => s.logout);

  const basePath = readEnv('VITE_IDP_BASE_URL')?.trim();

  const client = useMemo(() => {
    const c = createIdpClient({
      basePath: basePath ? basePath.replace(/\/+$/, '') : undefined,
      getAccessToken: () => useGlobalStore.getState().token ?? '',
      getRefreshToken: () => useGlobalStore.getState().refreshToken ?? '',
      getJti: () => useGlobalStore.getState().jti ?? '',
      login,
      logout,
      getAudience: () => resolveAudience(),
    });
    bindIdpClient(c);
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  // observe changes so memo dependencies are satisfied by React
  void token;
  void refreshToken;
  void jti;

  return <IdpContext.Provider value={client}>{children}</IdpContext.Provider>;
}

export function useIdp() {
  const ctx = useContext(IdpContext);
  if (!ctx) throw new Error('useIdp must be used within IdpProvider');
  return ctx;
}
